import ApiProtectedRequest from "utils/apiProtectedClient";
import { buildUrl } from "utils/buildUrl";
import type { Target, Goal, TargetSession } from "./types/aba";
import type { TargetSessionMetricsResponse } from "./types/charts";
import type {
  CreateTargetMarkerBody,
  DeleteMarkerScope,
  TargetMarker,
  UpdateTargetMarkerBody,
} from "./types/markers";

const EP_BASE = "/api";

/**
 * GET /targets/{targetId} returns the target with the parent goal embedded.
 * Per the API contract the embedded goal is the full Goal schema.
 */
export interface TargetWithGoal extends Target {
  goal: Goal;
}

export async function getTargetDetails(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.get<TargetWithGoal>(`${EP_BASE}/targets/${targetId}`);
}

export async function getListOfTargets(goalId: number, params: { includeArchived?: boolean } = {}) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  const url = buildUrl(`${EP_BASE}/goals/${goalId}/targets`, params);
  return await Request.get<Target[]>(url);
}

export async function createTarget(goalId: number, data: Partial<Target>) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/goals/${goalId}/targets`, data);
}

export async function updateTarget(targetId: number, data: Partial<Target>) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.put<Target>(`${EP_BASE}/targets/${targetId}`, data);
}

export async function deleteTarget(targetId: number): Promise<void> {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  await Request.delete(`${EP_BASE}/targets/${targetId}`);
}

export async function moveTarget(targetId: number, destinationGoalId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/targets/${targetId}/move`, {
    destination_goal_id: destinationGoalId,
  });
}

export async function archiveTarget(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/targets/${targetId}/archive`);
}

export async function unarchiveTarget(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/targets/${targetId}/unarchive`);
}

export async function duplicateTarget(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/targets/${targetId}/duplicate`);
}

export async function changeTargetStatus(targetId: number, payload: { new_status: string }) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Target>(`${EP_BASE}/targets/${targetId}/status`, payload);
}

export async function getTargetSessionMetrics(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.get<TargetSessionMetricsResponse>(
    `${EP_BASE}/targets/${targetId}/session-metrics`
  );
}

export async function listTargetSessions(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.get<TargetSession[]>(`${EP_BASE}/targets/${targetId}/target-sessions`);
}

// ---------------------------------------------------------------------------
// Target chart markers (CRUD)
//
// Session cookie + CSRF token are attached automatically by ApiProtectedRequest
// (axios `withCredentials` + `xsrfCookieName`), so callers don't pass them.
// ---------------------------------------------------------------------------

export async function listTargetMarkers(targetId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.get<TargetMarker[]>(`${EP_BASE}/targets/${targetId}/markers`);
}

export async function createTargetMarker(targetId: number, data: CreateTargetMarkerBody) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<TargetMarker>(`${EP_BASE}/targets/${targetId}/markers`, data);
}

export async function updateTargetMarker(
  targetId: number,
  markerId: number,
  data: UpdateTargetMarkerBody
) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.put<TargetMarker>(
    `${EP_BASE}/targets/${targetId}/markers/${markerId}`,
    data
  );
}

/**
 * Deletes a marker. Without `scope` the backend removes it for every target it
 * applies to; `"this-target"` removes only this target from a shared marker.
 */
export async function deleteTargetMarker(
  targetId: number,
  markerId: number,
  scope?: DeleteMarkerScope
): Promise<void> {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  const baseUrl = `${EP_BASE}/targets/${targetId}/markers/${markerId}`;
  await Request.delete(scope ? buildUrl(baseUrl, { scope }) : baseUrl);
}
